import { v4 as uuid } from 'uuid';
import { GoFormzServices } from '../../AppSettings';
import { apimCall } from '../../helpers/api';
import { getRawDateTimeFormatInfo } from '../../helpers/DateTimeUtility';
import { FieldPosition, TemplatePage } from '../../types/template-types';
import { getFieldFeatures } from '../helpers';
import {
  AnalyzeTemplateRequest,
  ApplyCheckboxGroupingAction,
  ApplyFieldSuggestionAction,
  DetectedField,
  PageBlobInfo,
  TemplateAnalysis
} from './templateeditor-ai-types';
import { FieldTypes, GetDisplayNameForFieldType } from '../../helpers/constants';

const THRESHOLD_PCT = 0.5;

/**
 * Analyze the template for possible fields to place.
 *
 * @param templateId The template id.
 * @param pages The pages of the template.
 * @param pageId Optional page id to analyze. If not provided, the first page will be analyzed.
 */
export async function analyzeTemplate(templateId: string, pages: TemplatePage[], pageId?: string): Promise<TemplateAnalysis> {
  if (pages.length === 0) {
    return { templateId, results: {} };
  }

  const pageToAnalyze = pages.find((p) => p.id === pageId) ?? pages[0];
  const pageBlobInfos: PageBlobInfo[] = [{ pageId: pageToAnalyze.id, imageBlobId: pageToAnalyze.fileId }];

  if (pageBlobInfos.length === 0) {
    return { templateId, results: {} };
  }

  const body: AnalyzeTemplateRequest = {
    templateId,
    pageBlobInfos
  };

  const response = await apimCall<TemplateAnalysis>(GoFormzServices.AIService, '/documents/template/analyze', 'POST', body);

  return response.data;
}

export function applyFieldSuggestions(
  templateAnalysis: TemplateAnalysis,
  pages: TemplatePage[],
  applyFieldSuggestionAction: ApplyFieldSuggestionAction,
  applyCheckboxFieldGroupingAction: ApplyCheckboxGroupingAction
): number {
  if (!templateAnalysis?.results) {
    return 0;
  }

  const features = getFieldFeatures();
  const rawDateFormat = getRawDateTimeFormatInfo();
  let numberPlaced = 0;

  Object.entries(templateAnalysis.results).forEach(([pageId, result]) => {
    const pageIdx = pages.findIndex((p) => p.id === pageId);

    if (pageIdx === -1) {
      return;
    }

    const { fieldPositions } = pages[pageIdx];
    const { fields } = result;
    // filter the fields on this page that overlaps the detected field by 50% or more
    // in the x and y axis
    const nonOverlappingFields = filterOverlappingFields(fieldPositions, fields);
    numberPlaced += nonOverlappingFields.length;

    if (nonOverlappingFields.length === 0) {
      return;
    }

    // this is the path into the template state where the page is stored
    const pageKeyPath = ['template', 'pages', pageIdx];

    // create a unique id for this action so it can be undone as a batch
    const batchUndoId = uuid();

    nonOverlappingFields.forEach((field) => {
      applyFieldSuggestionAction(pageKeyPath, field, features, rawDateFormat, batchUndoId);
    });

    applyCheckboxFieldGroupingAction(batchUndoId);
  });

  return numberPlaced;
}

/**
 * Yes I know there are two other flavors of this function (getFriendyNameForType and GetDisplayNameForFieldType).
 * This one is extra special because it will give you text or multi-line text instead of just "Text"!!
 *
 * @param fieldType the FieldType to get the name for.
 */
export function getFieldTypeName(fieldType: number): string {
  switch (fieldType) {
    case FieldTypes.TextBox:
      return 'Text';
    case FieldTypes.TextArea:
      return 'Multiline Text';
    default:
      return GetDisplayNameForFieldType(fieldType);
  }
}

// PRIVATES

function filterOverlappingFields(fieldPositions: FieldPosition[], fields: DetectedField[], thresholdPct = THRESHOLD_PCT): DetectedField[] {
  // no need to look if there are no fields on the page currently
  if (fieldPositions.length === 0) {
    return fields;
  }

  const fieldPostionBboxes = fieldPositions.map(({ x, y, w, h }) => ({ x, y, width: w, height: h }));

  const nonOverlappingFields = fields.filter(({ fieldBoundingBox: f }) => {
    // check each field against all existing positions
    const isOverlapping = fieldPostionBboxes.some((fp) => {
      // calculate overlap percentages
      const xOverlap = Math.min(f.x + f.width, fp.x + fp.width) - Math.max(f.x, fp.x);
      const yOverlap = Math.min(f.y + f.height, fp.y + fp.height) - Math.max(f.y, fp.y);

      // calculate overlap percentages relative to field dimensions
      const xOverlapPercent = xOverlap > 0 ? xOverlap / Math.min(f.width, fp.width) : 0;
      const yOverlapPercent = yOverlap > 0 ? yOverlap / Math.min(f.height, fp.height) : 0;

      // check if the overlap is 50% or more over any field position
      return xOverlapPercent >= thresholdPct && yOverlapPercent >= thresholdPct;
    });

    return !isOverlapping;
  });

  return nonOverlappingFields;
}
